import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DateTerm } from '../../../../utils/date'
import uiStates, {
  UiStateKey,
  UiStateScope,
} from '../../../../lib/commons/uiStates'
import DateVO from '../../../../vo/DateVO'
import { ROW_HEIGHT } from '../../../containers/BulkSheet'
import { UserBasic } from '../../../../domain/value-object/UserBasicVO'
import { TaskActualWorkApproveUserDto } from '../../../../applications/ports/taskActualWorkApproveRepository'
import { useTaskActualWorkApproveRepository } from '../../../../services/adaptors/taskActualWorkApproveAdaptor'
import queryString from 'query-string'

type StoredActualWorkingHoursPageState = {
  dateTerm: DateTerm
  rowHeight: ROW_HEIGHT
  userCode?: string
}

type UserState = {
  userUuid?: string
  userName?: string
  currentIndex: number
}

export type PageState = {
  initialized: boolean
  rowHeight: ROW_HEIGHT
  setRowHeight: (value: ROW_HEIGHT) => void
}

export type DateState = {
  displayDateTerm: DateTerm
  setDisplayDateTerm: (value: DateTerm) => void
}

export type UserNavigationState = {
  userUuid?: string
  userName?: string
  currentIndex: number
  totalCount: number
  onPagingUser: (index: number) => void
  setUserByUuid: (uuid: string) => void
}

export type DataState = {
  allUsersWithStatus: TaskActualWorkApproveUserDto[]
  refreshUsers: () => Promise<void>
}

export const usePageState = ({
  functionUuid,
  location,
}: {
  functionUuid: string
  location?: { search: string }
}) => {
  const { fetchProjectMembersForApproval } =
    useTaskActualWorkApproveRepository()
  const now = DateVO.now()
  const [rowHeight, setRowHeight] = useState<ROW_HEIGHT>(ROW_HEIGHT.SMALL)
  const [initialized, setInitialized] = useState(false)
  const [displayDateTerm, setDisplayDateTerm] = useState<DateTerm>({
    startDate: now.getFirstDayOfMonth().format(),
    endDate: now.getLastDayOfMonth().format(),
  })

  const [userState, setUserState] = useState<UserState>({
    userUuid: undefined,
    userName: undefined,
    currentIndex: 0,
  })

  const [totalCount, setTotalCount] = useState<number>(0)
  const [users, setUsers] = useState<UserBasic[]>([])
  const [allUsersWithStatus, setAllUsersWithStatus] = useState<
    TaskActualWorkApproveUserDto[]
  >([])

  const storePageState = useCallback(
    async (value: StoredActualWorkingHoursPageState) => {
      uiStates.update(
        {
          key: UiStateKey.PageState,
          scope: UiStateScope.User,
          value: JSON.stringify(value),
        },
        functionUuid
      )
    },
    [functionUuid]
  )

  useEffect(() => {
    if (!initialized) return

    const currentUser = users.find(user => user.uuid === userState.userUuid)
    storePageState({
      dateTerm: displayDateTerm,
      rowHeight,
      userCode: currentUser?.code,
    })
  }, [
    initialized,
    storePageState,
    displayDateTerm,
    rowHeight,
    userState.userUuid,
    users,
  ])

  const getQueryParams = useCallback(() => {
    if (!location?.search) return {}

    const qs = queryString.parse(location.search)
    const year =
      qs.year && !Array.isArray(qs.year) ? parseInt(qs.year) : undefined
    const month =
      qs.month && !Array.isArray(qs.month) ? parseInt(qs.month) : undefined
    const userCode =
      qs.userCode && !Array.isArray(qs.userCode) ? qs.userCode : undefined

    return { year, month, userCode }
  }, [location?.search])

  const getStoredState =
    useCallback(async (): Promise<StoredActualWorkingHoursPageState | null> => {
      try {
        const response = await uiStates.get({
          applicationFunctionUuid: functionUuid,
          key: UiStateKey.PageState,
          scope: UiStateScope.User,
        })

        const stored = response.json
        if (!stored.value) return null

        return JSON.parse(stored.value)
      } catch (error) {
        console.warn('Failed to restore UI state:', error)
        return null
      }
    }, [functionUuid])

  const determineDateTerm = useCallback(
    (
      queryParams: any,
      storedState: StoredActualWorkingHoursPageState | null
    ): DateTerm => {
      const { year, month } = queryParams

      // QueryStringで指定されている場合
      if (year && month && month >= 1 && month <= 12) {
        const firstDay = new DateVO(
          `${year}-${String(month).padStart(2, '0')}-01`
        )
        const lastDay = firstDay.addMonths(1).addDays(-1)
        return {
          startDate: firstDay.format(),
          endDate: lastDay.format(),
        }
      }
      if (storedState?.dateTerm) {
        return storedState.dateTerm
      }

      // デフォルト: 現在月
      return {
        startDate: now.getFirstDayOfMonth().format(),
        endDate: now.getLastDayOfMonth().format(),
      }
    },
    [now]
  )

  const fetchUsers = useCallback(
    async (startDate: string): Promise<UserBasic[]> => {
      const formattedStartDate = new DateVO(startDate).formatForApi()
      const response = await fetchProjectMembersForApproval(formattedStartDate)
      setAllUsersWithStatus(response)
      const responseUsers = response.map(
        v =>
          ({
            uuid: v.projectMemberDetail.user.uuid,
            name: v.projectMemberDetail.user.name,
            code: v.projectMemberDetail.user.code,
            iconUrl: v.projectMemberDetail.user.iconUrl,
          }) as UserBasic
      )
      return responseUsers
    },
    [fetchProjectMembersForApproval]
  )

  const determineUser = useCallback(
    (
      queryParams: any,
      storedState: StoredActualWorkingHoursPageState | null,
      userList: UserBasic[]
    ) => {
      const { userCode: queryUserCode } = queryParams

      if (userList.length === 0) {
        setUserState({
          userUuid: undefined,
          userName: undefined,
          currentIndex: 0,
        })
        return
      }

      if (queryUserCode) {
        const targetUser = userList.find(user => user.code === queryUserCode)
        if (targetUser) {
          const userIndex = userList.findIndex(
            user => user.code === queryUserCode
          )
          setUserState({
            userUuid: targetUser.uuid,
            userName: targetUser.name,
            currentIndex: userIndex + 1,
          })
          return
        }
      }

      if (storedState?.userCode) {
        const targetUser = userList.find(
          user => user.code === storedState.userCode
        )
        if (targetUser) {
          const userIndex = userList.findIndex(
            user => user.code === storedState.userCode
          )
          setUserState({
            userUuid: targetUser.uuid,
            userName: targetUser.name,
            currentIndex: userIndex + 1,
          })
          return
        }
      }

      setUserState({
        userUuid: userList[0].uuid,
        userName: userList[0].name,
        currentIndex: 1,
      })
    },
    []
  )

  const initializePageState = async () => {
    const queryParams = getQueryParams()
    const storedState = await getStoredState()

    const finalDateTerm = determineDateTerm(queryParams, storedState)
    setDisplayDateTerm(finalDateTerm)

    if (storedState?.rowHeight) {
      setRowHeight(storedState.rowHeight)
    }
    const userList = await fetchUsers(finalDateTerm.startDate || '')
    setUsers(userList)
    determineUser(queryParams, storedState, userList)

    setInitialized(true)
  }

  useEffect(() => {
    initializePageState()
  }, [])

  const refreshUsers = useCallback(async () => {
    if (!displayDateTerm.startDate) return

    await fetchUsers(displayDateTerm.startDate)
    setUsers(prev => prev)
  }, [displayDateTerm.startDate, fetchUsers])

  useEffect(() => {
    if (!initialized || !displayDateTerm.startDate) return
    refreshUsers()
  }, [displayDateTerm.startDate, initialized, refreshUsers])

  useEffect(() => {
    if (!initialized) return

    setTotalCount(users.length)

    if (users.length === 0) {
      setUserState({
        userUuid: undefined,
        userName: undefined,
        currentIndex: 0,
      })
      return
    }

    const currentUserIndex = users.findIndex(u => u.uuid === userState.userUuid)

    if (currentUserIndex >= 0) {
      if (userState.currentIndex !== currentUserIndex + 1) {
        setUserState(prev => ({
          ...prev,
          currentIndex: currentUserIndex + 1,
        }))
      }
    } else {
      setUserState({
        userUuid: users[0].uuid,
        userName: users[0].name,
        currentIndex: 1,
      })
    }
  }, [users, initialized, userState.userUuid, userState.currentIndex])

  const onPagingUser = useCallback(
    (index: number) => {
      const nextUser = users[index - 1]
      if (nextUser) {
        setUserState(prev => ({
          ...prev,
          currentIndex: index,
          userUuid: nextUser.uuid,
          userName: nextUser.name,
        }))
      }
    },
    [users]
  )

  const setUserByUuid = useCallback(
    (uuid: string) => {
      const userIndex = users.findIndex(u => u.uuid === uuid)
      if (userIndex >= 0) {
        setUserState({
          userUuid: users[userIndex].uuid,
          userName: users[userIndex].name,
          currentIndex: userIndex + 1,
        })
      }
    },
    [users]
  )

  return {
    pageState: {
      initialized,
      rowHeight,
      setRowHeight,
    },
    dateState: {
      displayDateTerm,
      setDisplayDateTerm,
    },
    userState: {
      userUuid: userState.userUuid,
      userName: userState.userName,
      currentIndex: userState.currentIndex,
      totalCount,
      onPagingUser,
      setUserByUuid,
    },
    dataState: {
      allUsersWithStatus,
      refreshUsers,
    },
  }
}
